/**
 * Leaflet map layer runtime: init, markers/routes/clusters, rail highlight,
 * dwell heatmap, resize, city-replay overlay. Extracted from App.js (plan 2.9).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import Supercluster from 'supercluster';
import moment from 'moment';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';
import { TILE_URL, TILE_ATTRIBUTION } from '../config/mapTiles';
import {
  CHECKIN_CLUSTER_OPTIONS,
  normalizeCoords,
  buildCheckinClusterFeatures,
  getClusterMarkerSize,
  isMapLayerVisible,
  getRouteDisplayStyle,
  applyRailHighlightToLayer,
  escapeHtml,
  simplifyDrawableRoute,
} from '../MapRuntime';
import { aggregateDwellTime, buildDwellRoutePoints } from '../dwellTime';
import {
  resetReplayExportState,
  REPLAY_EXPORT_STATE,
} from '../replayExportState';
import { exportReplayVideo } from '../cityReplay';
import { t } from '../i18n';

const defaultIcon = new L.Icon({
  iconRetinaUrl,
  iconUrl,
  shadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = defaultIcon;

export function useMapLayers({
  user,
  preserveViewport,
  clusterMarkers,
  safeFetchedData,
  selectedFilterTypes,
  visibleRouteItems,
  routeItemsById,
  selectedRouteId,
  currentFilters,
  handleRouteSelect,
  tagFilterVenueIds,
  atlasVivoActive,
  setAtlasSelection,
  railHoverPosition,
  railFocusPosition,
  showDwellHeatmap,
  setSelectedDwellBin,
  cityReplay,
  setCityReplay,
  replayPlaying,
  setReplayPlaying,
  replaySpeed,
  setReplaySpeed,
  replayIndex,
  setReplayIndex,
  replayExportState,
  setReplayExportState,
  setReplayExportError,
  routeFocusRequest,
  appTarget,
  isMobileSidebarOpen,
  calendarPanelMode,
}) {
  const [mapInitialized, setMapInitialized] = useState(false);
  const mapRef = useRef(null);
  const markersLayer = useRef(null);
  const polylinesLayer = useRef(null);
  const dwellLayer = useRef(null);
  const routeLayersRef = useRef({});
  const replayAnimRef = useRef(null);
  const replayStartTimeRef = useRef(null);
  const replayAccumMsRef = useRef(0);
  const replayLayer = useRef(null);

  const focusRouteById = useCallback((routeId) => {
    const routeLayer = routeLayersRef.current[routeId];
    if (!mapRef.current || !routeLayer) return;

    mapRef.current.closePopup();
    const bounds = routeLayer.getBounds();
    if (bounds.isValid()) {
      mapRef.current.flyToBounds(bounds, { padding: [50, 50], maxZoom: 17 });
    }
    window.setTimeout(() => routeLayer.openPopup(), 300);
  }, []);

  const clearMapOnLogout = useCallback(() => {
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
      markersLayer.current = null;
      polylinesLayer.current = null;
      dwellLayer.current = null;
      routeLayersRef.current = {};
    }
    setMapInitialized(false);
  }, []);


  // --- Map Initialization and Viewport Persistence ---
  useEffect(() => {
    const mapElement = document.getElementById('map');
    if (!mapRef.current && !mapInitialized && mapElement) {
      mapRef.current = L.map('map', {
        inertia: false,
        minZoom: 2,
        worldCopyJump: true,
        maxBoundsViscosity: 1,
        // Renderiza camadas vetoriais (rotas) em canvas em vez de SVG. Com
        // centenas de rotas de ~1000 pontos cada (~1M pontos), o canvas é
        // muito mais rápido para desenhar e dar pan/zoom.
        preferCanvas: true,
      });
      const savedViewportStr = localStorage.getItem("viewport");
      if (savedViewportStr && preserveViewport) { // Only load if preserve is on
        const savedViewport = JSON.parse(savedViewportStr);
        mapRef.current.setView(savedViewport.center, savedViewport.zoom);
      } else {
        mapRef.current.setView([0, 0], 2);
      }
      L.tileLayer(TILE_URL, {
        maxZoom: 19,
        attribution: TILE_ATTRIBUTION,
      }).addTo(mapRef.current);
      mapRef.current.zoomControl.setPosition('topright');
      mapRef.current.setMaxBounds([[-90, -180], [90, 180]]);
      markersLayer.current = L.featureGroup().addTo(mapRef.current);
      polylinesLayer.current = L.featureGroup().addTo(mapRef.current);
      dwellLayer.current = L.featureGroup().addTo(mapRef.current);
      setMapInitialized(true);
    }
  }, [mapInitialized, preserveViewport, user]);

  useEffect(() => {
    if (mapRef.current) {
      const saveViewport = () => {
        const center = mapRef.current.getCenter();
        const zoom = mapRef.current.getZoom();
        localStorage.setItem("viewport", JSON.stringify({ center: [center.lat, center.lng], zoom }));
      };
      if (preserveViewport) {
        mapRef.current.on("moveend", saveViewport);
      } else {
        mapRef.current.off("moveend", saveViewport);
        // Optionally remove viewport if preserve is turned off
        // localStorage.removeItem("viewport");
      }
      return () => {
        if (mapRef.current) { // Check if mapRef.current still exists
            mapRef.current.off("moveend", saveViewport);
        }
      };
    }
  }, [preserveViewport]);




  // --- Map Updates ---
  useEffect(() => {
    if (!mapInitialized || !mapRef.current || !markersLayer.current || !polylinesLayer.current) return;

    const map = mapRef.current;
    markersLayer.current.clearLayers();
    polylinesLayer.current.clearLayers();
    routeLayersRef.current = {};

    const { concerts } = safeFetchedData;
    const checkins = tagFilterVenueIds
      ? (safeFetchedData.checkins || []).filter(c => tagFilterVenueIds.has(String(c.id)))
      : safeFetchedData.checkins;
    const selectedTypes = selectedFilterTypes;
    const checkinFeatures = selectedTypes.includes('checkin') && isMapLayerVisible(currentFilters.focusedKey, 'checkin')
      ? buildCheckinClusterFeatures(checkins)
      : [];
    const checkinBounds = L.latLngBounds([]);
    let renderCheckinClusters = null;

    if (checkinFeatures.length > 0) {
      checkinFeatures.forEach(feature => {
        const [lon, lat] = feature.geometry.coordinates;
        checkinBounds.extend([lat, lon]);
      });

      if (clusterMarkers) {
        const checkinLayer = L.featureGroup().addTo(markersLayer.current);
        const clusterIndex = new Supercluster(CHECKIN_CLUSTER_OPTIONS).load(checkinFeatures);

        renderCheckinClusters = () => {
          checkinLayer.clearLayers();
          const bounds = map.getBounds();
          const south = Math.max(-90, bounds.getSouth());
          const north = Math.min(90, bounds.getNorth());
          const west = bounds.getWest();
          const east = bounds.getEast();
          const zoom = Math.max(0, Math.min(20, Math.floor(map.getZoom())));
          const queryBounds = west <= east
            ? [[west, south, east, north]]
            : [[west, south, 180, north], [-180, south, east, north]];
          const clusters = queryBounds.flatMap(bbox => clusterIndex.getClusters(bbox, zoom));

          clusters.forEach(feature => {
            const [lon, lat] = feature.geometry.coordinates;
            if (feature.properties.cluster) {
              const pointCount = feature.properties.point_count;
              const markerSize = getClusterMarkerSize(pointCount);
              const marker = L.marker([lat, lon], {
                icon: L.divIcon({
                  className: 'map-checkin-cluster',
                  html: `<span>${feature.properties.point_count_abbreviated}</span>`,
                  iconSize: [markerSize, markerSize],
                }),
                title: t('web.map.placesCount', { count: pointCount }),
              });

              marker
                .bindTooltip(t('web.map.placesCount', { count: pointCount }), { direction: 'top', offset: [0, -markerSize / 2] })
                .on('click', () => {
                  const expansionZoom = Math.min(17, clusterIndex.getClusterExpansionZoom(feature.properties.cluster_id));
                  map.flyTo([lat, lon], expansionZoom);
                })
                .addTo(checkinLayer);
              return;
            }

            const checkin = checkins[feature.properties.checkinIndex];
            const markerRadius = zoom >= 15 ? 6 : zoom >= 11 ? 5 : 4;
            const checkinMarker = L.circleMarker([lat, lon], {
              radius: markerRadius,
              color: '#8ec5ff',
              weight: 1,
              fillColor: '#3498db',
              fillOpacity: 0.82,
            });
            checkinMarker._railHighlightDate = checkin.date;
            checkinMarker._railHighlightBase = {
              radius: markerRadius,
              color: '#8ec5ff',
              weight: 1,
              fillColor: '#3498db',
              fillOpacity: 0.82,
            };
            if (atlasVivoActive) {
              checkinMarker.on('click', () => setAtlasSelection({ kind: 'place', data: checkin }));
            } else {
              checkinMarker.bindPopup([
                `<b>${escapeHtml(checkin.venue)}</b>`,
                t('web.map.categoryLine', { value: escapeHtml(checkin.category) }),
                t('web.map.dateLine', { value: moment(checkin.date).format('DD/MM/YYYY') }),
                t('web.map.addressLine', { value: escapeHtml(checkin.formatted_address) }),
                `<button class="popup-edit-tags-btn" data-venue-id="${escapeHtml(checkin.id)}" data-venue-name="${escapeHtml(checkin.venue)}">${t('web.map.editTags')}</button>`,
              ].join('<br>'));
            }
            checkinMarker.addTo(checkinLayer);
          });
        };

        renderCheckinClusters();
        map.on('moveend', renderCheckinClusters);
      } else {
        checkinFeatures.forEach(feature => {
          const [lon, lat] = feature.geometry.coordinates;
          const checkin = checkins[feature.properties.checkinIndex];
          const checkinMarker = L.circleMarker([lat, lon], {
            radius: 5,
            color: '#8ec5ff',
            weight: 1,
            fillColor: '#3498db',
            fillOpacity: 0.82,
          });
          checkinMarker._railHighlightDate = checkin.date;
          checkinMarker._railHighlightBase = {
            radius: 5,
            color: '#8ec5ff',
            weight: 1,
            fillColor: '#3498db',
            fillOpacity: 0.82,
          };
          if (atlasVivoActive) {
            checkinMarker.on('click', () => setAtlasSelection({ kind: 'place', data: checkin }));
          } else {
            checkinMarker.bindPopup([
              `<b>${escapeHtml(checkin.venue)}</b>`,
              t('web.map.categoryLine', { value: escapeHtml(checkin.category) }),
              t('web.map.dateLine', { value: moment(checkin.date).format('DD/MM/YYYY') }),
              t('web.map.addressLine', { value: escapeHtml(checkin.formatted_address) }),
            ].join('<br>'));
          }
          checkinMarker.addTo(markersLayer.current);
        });
      }
    }

    if (selectedTypes.includes('concerts') && isMapLayerVisible(currentFilters.focusedKey, 'concerts') && concerts) {
      concerts.forEach(concert => {
        const [lat, lon] = normalizeCoords([Number(concert.latitude), Number(concert.longitude)]);
        if (Number.isFinite(lat) && Number.isFinite(lon)) {
          const concertMarker = L.circleMarker([lat, lon], {
            radius: 5,
            color: '#1abc9c',
            weight: 1,
            fillColor: '#1abc9c',
            fillOpacity: 0.85,
          });
          concertMarker._railHighlightDate = concert.event_date;
          concertMarker._railHighlightBase = {
            radius: 5,
            color: '#1abc9c',
            weight: 1,
            fillColor: '#1abc9c',
            fillOpacity: 0.85,
          };
          if (atlasVivoActive) {
            concertMarker.on('click', () => setAtlasSelection({ kind: 'concert', data: concert }));
          } else {
            concertMarker.bindPopup([
              `<b>${escapeHtml(concert.artist_name || t('web.map.concertFallback'))}</b>`,
              escapeHtml(concert.venue_name || ''),
              `${escapeHtml(concert.city_name || '')}${concert.country_name ? `, ${escapeHtml(concert.country_name)}` : ''}`,
              t('web.map.dateLine', { value: moment(concert.event_date).format('DD/MM/YYYY') }),
            ].join('<br>'));
          }
          concertMarker.addTo(markersLayer.current);
        }
      });
    }

    const photos = safeFetchedData.photos || [];
    if (selectedTypes.includes('photos') && isMapLayerVisible(currentFilters.focusedKey, 'photos') && photos.length > 0) {
      photos.forEach(photo => {
        const lat = Number(photo.latitude);
        const lon = Number(photo.longitude);
        if (Number.isFinite(lat) && Number.isFinite(lon)) {
          const photoMarker = L.circleMarker([lat, lon], {
            radius: 6,
            color: '#f39c12',
            weight: 2,
            fillColor: '#f39c12',
            fillOpacity: 0.7,
          });
          photoMarker._railHighlightDate = photo.photo_date;
          photoMarker._railHighlightBase = {
            radius: 6,
            color: '#f39c12',
            weight: 2,
            fillColor: '#f39c12',
            fillOpacity: 0.7,
          };
          photoMarker
            .bindPopup([
              `<b>${escapeHtml(photo.title || t('web.map.photoFallback'))}</b>${photo.description ? `<br>${escapeHtml(photo.description)}` : ''}`,
              t('web.map.dateLine', { value: moment(photo.photo_date).format('DD/MM/YYYY') }),
            ].join('<br>'))
            .addTo(markersLayer.current);
        }
      });
    }

    const selectedRouteItem = routeItemsById.get(selectedRouteId);
    const hasVisibleSelectedRoute = Boolean(
      selectedRouteItem && isMapLayerVisible(currentFilters.focusedKey, selectedRouteItem.eventType)
    );

    visibleRouteItems.forEach(routeItem => {
      if (!isMapLayerVisible(currentFilters.focusedKey, routeItem.eventType)) return;
      const rawDrawable = routeItem.lineSegments || routeItem.points;
      if (!rawDrawable || rawDrawable.length === 0) return;
      const drawablePoints = simplifyDrawableRoute(rawDrawable);

      const isActive = routeItem.id === selectedRouteId;
      const routeStyle = getRouteDisplayStyle(routeItem.style, isActive, hasVisibleSelectedRoute);
      const polyline = L.polyline(
        drawablePoints,
        routeStyle,
      )
        .bindPopup(routeItem.popupHtml)
        .on('click', () => handleRouteSelect(routeItem.id));
      polyline._railHighlightDate = routeItem.date;
      polyline._railHighlightBase = routeStyle;
      polyline._railHighlightMode = null;

      polyline.addTo(polylinesLayer.current);
      if (isActive) polyline.bringToFront();
      routeLayersRef.current[routeItem.id] = polyline;
    });

    if (!preserveViewport) {
        let bounds = L.latLngBounds([]);
        if (checkinBounds.isValid()) {
            bounds.extend(checkinBounds);
        }
        if (markersLayer.current.getLayers().length > 0) {
            bounds.extend(markersLayer.current.getBounds());
        }
        if (polylinesLayer.current.getLayers().length > 0) {
            polylinesLayer.current.eachLayer(layer => { // Iterate to handle multiple polylines
                bounds.extend(layer.getBounds());
            });
        }
        if (bounds.isValid()) {
            mapRef.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 });
        } else if (markersLayer.current.getLayers().length === 0 && polylinesLayer.current.getLayers().length === 0) {
             if (!localStorage.getItem("viewport")) { // Only reset if no saved viewport
                mapRef.current.setView([0,0], 2);
             }
        }
    }

    return () => {
      if (renderCheckinClusters) map.off('moveend', renderCheckinClusters);
    };
  }, [safeFetchedData, selectedFilterTypes, visibleRouteItems, routeItemsById, currentFilters.focusedKey, handleRouteSelect, mapInitialized, preserveViewport, clusterMarkers, tagFilterVenueIds, atlasVivoActive]);


  // Restyle only — avoid clearLayers + Supercluster rebuild on route select.
  useEffect(() => {
    if (!mapInitialized) return;
    const selectedRouteItem = routeItemsById.get(selectedRouteId);
    const hasVisibleSelectedRoute = Boolean(
      selectedRouteItem && isMapLayerVisible(currentFilters.focusedKey, selectedRouteItem.eventType)
    );
    Object.entries(routeLayersRef.current).forEach(([id, polyline]) => {
      const routeItem = routeItemsById.get(id);
      if (!routeItem || !polyline?.setStyle) return;
      const isActive = id === selectedRouteId;
      const routeStyle = getRouteDisplayStyle(routeItem.style, isActive, hasVisibleSelectedRoute);
      polyline.setStyle(routeStyle);
      polyline._railHighlightBase = routeStyle;
      polyline._railHighlightMode = null;
      if (isActive) polyline.bringToFront();
    });
  }, [mapInitialized, selectedRouteId, routeItemsById, currentFilters.focusedKey]);


  const railHighlightPosition = railFocusPosition ?? railHoverPosition;
  const railHighlightRange = useMemo(() => ({
    start: currentFilters.startDate,
    end: currentFilters.endDate,
  }), [currentFilters.startDate, currentFilters.endDate]);


  useEffect(() => {
    if (!mapInitialized || !markersLayer.current || !polylinesLayer.current) return;

    let rafId = 0;
    const apply = () => {
      const visitLayer = (layer) => {
        if (layer?._railHighlightDate != null) {
          applyRailHighlightToLayer(layer, layer._railHighlightDate, railHighlightPosition, railHighlightRange);
          return;
        }
        if (typeof layer?.eachLayer === 'function') {
          layer.eachLayer(visitLayer);
        }
      };

      markersLayer.current.eachLayer(visitLayer);
      polylinesLayer.current.eachLayer(visitLayer);
    };

    rafId = window.requestAnimationFrame(apply);
    return () => window.cancelAnimationFrame(rafId);
  }, [mapInitialized, railHighlightPosition, railHighlightRange]);


  const dwellBins = useMemo(() => {
    if (!showDwellHeatmap) return [];
    const routePoints = buildDwellRoutePoints(safeFetchedData);
    return aggregateDwellTime(routePoints, safeFetchedData.checkins || []);
  }, [showDwellHeatmap, safeFetchedData]);


  useEffect(() => {
    if (!mapInitialized || !mapRef.current || !dwellLayer.current) return;
    dwellLayer.current.clearLayers();
    if (!showDwellHeatmap || dwellBins.length === 0) return;

    const maxMinutes = Math.max(...dwellBins.map((b) => b.totalMinutes));
    dwellBins.forEach((bin) => {
      const ratio = maxMinutes > 0 ? bin.totalMinutes / maxMinutes : 0;
      const radius = 8 + ratio * 32;
      L.circleMarker([bin.lat, bin.lon], {
        radius,
        color: '#e67e22',
        weight: 1,
        fillColor: '#e67e22',
        fillOpacity: 0.25 + ratio * 0.35,
      })
        .on('click', () => setSelectedDwellBin(bin))
        .addTo(dwellLayer.current);
    });
  }, [mapInitialized, showDwellHeatmap, dwellBins]);


  useEffect(() => {
    if (routeFocusRequest?.routeId) {
      focusRouteById(routeFocusRequest.routeId);
    }
  }, [routeFocusRequest, focusRouteById, visibleRouteItems, selectedRouteId]);


  const handleReplayRestart = useCallback(() => {
    setReplayIndex(0);
    setReplayPlaying(false);
    replayAccumMsRef.current = 0;
    replayStartTimeRef.current = null;
    setReplayExportState(resetReplayExportState());
    setReplayExportError('');
    if (replayLayer.current) {
      mapRef.current?.removeLayer(replayLayer.current);
      replayLayer.current = null;
    }
  }, []);

  const handleReplayPlay = useCallback(() => {
    setReplayPlaying(true);
    replayStartTimeRef.current = performance.now();
  }, []);

  const handleReplayPause = useCallback(() => {
    setReplayPlaying(false);
    if (replayStartTimeRef.current != null) {
      replayAccumMsRef.current += performance.now() - replayStartTimeRef.current;
    }
    replayStartTimeRef.current = null;
  }, []);

  const handleReplayExport = useCallback(async () => {
    if (!cityReplay || replayExportState === REPLAY_EXPORT_STATE.RECORDING) return;
    if (replayExportState === REPLAY_EXPORT_STATE.UNSUPPORTED) return;
    setReplayExportState(REPLAY_EXPORT_STATE.RECORDING);
    setReplayExportError('');
    try {
      const mapElement = document.getElementById('map');
      const blob = await exportReplayVideo(
        mapElement,
        replayLayer.current,
        5000,
        () => {},
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `replay-${cityReplay.city.replace(/\s+/g, '-').toLowerCase()}.webm`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setReplayExportState(REPLAY_EXPORT_STATE.DONE);
    } catch (err) {
      setReplayExportError(err.message || t('web.map.exportVideoError'));
      setReplayExportState(REPLAY_EXPORT_STATE.ERROR);
    }
  }, [cityReplay, replayExportState]);


  useEffect(() => {
    if (!cityReplay || !replayPlaying || !mapRef.current) return;

    const timeline = cityReplay.timeline;
    if (timeline.length === 0 || replayIndex >= timeline.length) {
      setReplayPlaying(false);
      return;
    }

    const MS_PER_EVENT = 300;

    const frame = () => {
      const now = performance.now();
      const start = replayStartTimeRef.current || now;
      const elapsed = replayAccumMsRef.current + (now - start);
      const targetIndex = Math.min(
        Math.floor((elapsed * replaySpeed) / MS_PER_EVENT),
        timeline.length,
      );

      if (targetIndex > replayIndex) {
        setReplayIndex(targetIndex);
      }

      if (targetIndex >= timeline.length) {
        setReplayPlaying(false);
        replayAccumMsRef.current = 0;
        replayStartTimeRef.current = null;
        return;
      }

      if (replayStartTimeRef.current != null) {
        replayAnimRef.current = requestAnimationFrame(frame);
      }
    };

    replayAnimRef.current = requestAnimationFrame(frame);

    return () => {
      if (replayAnimRef.current) {
        cancelAnimationFrame(replayAnimRef.current);
        replayAnimRef.current = null;
      }
    };
  }, [cityReplay, replayPlaying, replaySpeed, replayIndex]);


  useEffect(() => {
    if (!cityReplay || !mapRef.current) return;

    const timeline = cityReplay.timeline;
    const map = mapRef.current;

    if (replayLayer.current) {
      map.removeLayer(replayLayer.current);
    }
    replayLayer.current = L.featureGroup().addTo(map);

    for (let i = 0; i < replayIndex && i < timeline.length; i++) {
      const event = timeline[i];
      if (event.type === 'checkin' || event.type === 'concert') {
        const d = event.data;
        if (d.lat != null && d.lon != null) {
          L.circleMarker([d.lat, d.lon], {
            radius: 6,
            color: event.type === 'concert' ? '#f39c12' : '#3498db',
            weight: 1.5,
            fillColor: event.type === 'concert' ? '#f39c12' : '#3498db',
            fillOpacity: 0.9,
          }).addTo(replayLayer.current);
        }
      } else if (event.type === 'route') {
        const points = event.data.points || [];
        if (points.length >= 2) {
          L.polyline(points, {
            color: '#e74c3c',
            weight: 3,
            opacity: 0.9,
          }).addTo(replayLayer.current);
        }
      } else if (event.type === 'photo') {
        const d = event.data;
        if (d.latitude != null && d.longitude != null) {
          L.circleMarker([d.latitude, d.longitude], {
            radius: 5,
            color: '#2ecc71',
            weight: 1,
            fillColor: '#2ecc71',
            fillOpacity: 0.8,
          }).addTo(replayLayer.current);
        }
      }
    }
  }, [cityReplay, replayIndex]);


  // Resize map handler
  useEffect(() => {
    const handleResize = () => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
      }
    };
    window.addEventListener('resize', handleResize);
    // Initial call
    if (mapRef.current) mapRef.current.invalidateSize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
      }
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [appTarget, isMobileSidebarOpen, calendarPanelMode]);


  return {
    mapRef,
    markersLayer,
    polylinesLayer,
    dwellLayer,
    routeLayersRef,
    replayLayer,
    replayAnimRef,
    replayStartTimeRef,
    replayAccumMsRef,
    mapInitialized,
    setMapInitialized,
    focusRouteById,
    clearMapOnLogout,
    handleReplayRestart,
    handleReplayPlay,
    handleReplayPause,
    handleReplayExport,
  };
}
